// src/pages/PromptsPage.js
import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Save, Settings } from 'lucide-react';
import toast from 'react-hot-toast';
import { getPrompts, getPrompt, setPrompt } from '../utils/api';

const PROMPT_HINTS = {
  'expenses-ungrouped-any-time-frame': {
    input: 'All Expenses MTD (.csv)',
    output: 'Word (.docx) — accountant review memo',
  },
  'expense-analysis-categorization': {
    input: 'All Expenses MTD (.csv)',
    output: 'Word (.docx) — expense analysis & categorization report',
  },
  'grouping-expense-cfo-commentary': {
    input: 'All Expenses MTD (.csv)',
    output: 'Word (.docx) — CFO accounting review',
  },
  'purchase-by-vendor-summary-for-team': {
    input: 'Purchases by Vendor Detail — Month to Date (Weekly).csv',
    output: 'Word (.docx) — team payroll & contractor memo',
  },
  'operational-expenses': {
    input: 'All Expenses MTD (.csv)',
    output: 'Word (.docx) — operational expenses breakdown by category',
  },
  'rehab-expenses': {
    input: 'All Expenses MTD (.csv)',
    output: 'Word (.docx) — rehab/construction costs by category & week',
  },
  'portfolio-dashboard': {
    input: 'Profit and Loss by Customer + Phase 2 Properties (pair upload)',
    output: 'Excel (.xlsx) — portfolio dashboard',
  },
  'budget-vs-actual': {
    input: 'Budget.xlsx + Profit and Loss.xlsx (pair upload)',
    output: 'Excel (.xlsx) — budget vs actual with pace status & coloring',
  },
};

export default function PromptsPage() {
  const qc = useQueryClient();
  const [selectedKey, setSelectedKey] = useState(null);
  const [editText, setEditText] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  // Fetch all prompts
  const { data: promptsData, isLoading: promptsLoading } = useQuery({
    queryKey: ['prompts'],
    queryFn: getPrompts,
  });

  // Fetch selected prompt's full text
  const { data: promptData, isLoading: promptLoading } = useQuery({
    queryKey: ['prompt', selectedKey],
    queryFn: () => getPrompt(selectedKey),
    enabled: !!selectedKey,
  });

  // Auto-select first prompt when prompts load
  useEffect(() => {
    if (promptsData?.prompts && !selectedKey) {
      setSelectedKey(promptsData.prompts[0]?.key);
    }
  }, [promptsData, selectedKey]);

  // Update editText when prompt changes
  useEffect(() => {
    if (promptData?.prompt) {
      setEditText(promptData.prompt);
      setIsDirty(false);
    }
  }, [promptData]);

  const saveMutation = useMutation({
    mutationFn: () => setPrompt(selectedKey, editText),
    onSuccess: () => {
      toast.success('Prompt saved!');
      setIsDirty(false);
      qc.invalidateQueries({ queryKey: ['prompt', selectedKey] });
      qc.invalidateQueries({ queryKey: ['prompts'] });
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Save failed'),
  });

  const prompts = promptsData?.prompts || [];
  const selectedPrompt = prompts.find(p => p.key === selectedKey);
  const hint = selectedKey ? PROMPT_HINTS[selectedKey] : null;

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title">Analysis Prompts</h1>
        <p className="page-subtitle">
          Customize the instructions Claude receives when analyzing uploaded reports.
        </p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 16, alignItems: 'start' }}>
        {/* Prompt selector */}
        <div className="card" style={{ padding: 0 }}>
          <div className="card-title" style={{ padding: '16px 16px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Settings size={13} /> Report Types
          </div>
          {promptsLoading ? (
            <div style={{ padding: 16, textAlign: 'center' }}>
              <span className="spinner" />
            </div>
          ) : (
            prompts.map((p) => (
              <button
                key={p.key}
                onClick={() => setSelectedKey(p.key)}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '10px 16px',
                  textAlign: 'left',
                  background: selectedKey === p.key ? 'var(--gold-dim)' : 'transparent',
                  color: selectedKey === p.key ? 'var(--gold)' : 'var(--text)',
                  border: 'none',
                  borderTop: '1px solid var(--border)',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontFamily: 'var(--font-sans)',
                }}
              >
                <div style={{ fontWeight: 600 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                  {p.length?.toLocaleString()} chars
                </div>
              </button>
            ))
          )}
        </div>

        {/* Prompt editor */}
        <div className="card">
          <div className="card-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span>{selectedPrompt?.name || 'Select a prompt'}</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {isDirty && <span className="badge badge-pending" style={{ fontSize: 11 }}>Unsaved</span>}
              <button
                className="btn btn-primary btn-sm"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending || !isDirty || !selectedKey}
              >
                {saveMutation.isPending ? <span className="spinner" /> : <Save size={13} />}
                Save
              </button>
            </div>
          </div>

          <p className="text-muted" style={{ marginBottom: 12, fontSize: 12 }}>
            Sent to Claude as the <code style={{ background: 'var(--navy-3)', padding: '1px 5px', borderRadius: 3 }}>system</code> message
            when the matching file is uploaded.
            {hint && (
              <>
                {' '}Input: <strong style={{ color: 'var(--text)' }}>{hint.input}</strong>.
                {' '}Output: <strong style={{ color: 'var(--text)' }}>{hint.output}</strong>.
              </>
            )}
          </p>

          {promptLoading || !selectedKey ? (
            <div className="empty-state"><span className="spinner" /></div>
          ) : (
            <textarea
              className="input"
              rows={24}
              value={editText}
              onChange={e => { setEditText(e.target.value); setIsDirty(true); }}
              placeholder="Enter the analysis instructions Claude should follow…"
            />
          )}

          <div className="text-muted" style={{ marginTop: 10, fontSize: 11.5 }}>
            Prompt text matches the client copy in needed_prompts.txt. Server restart applies code defaults; Save here overrides until the next deploy.
          </div>
        </div>
      </div>
    </div>
  );
}
